/**
 * A phone the host removed. It may ask back in once; the host sees the request and lets
 * it in or denies it, and one left unanswered lapses into a denial on the server.
 * `onAdmitted` rejoins with the same token.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';

type Phase = 'kicked' | 'pending' | 'denied' | 'admitted' | 'error';

/** Often enough to feel live, rarely enough to stay well under the route's limit. */
const POLL_MS = 4000;

export function Kicked({
  code,
  token,
  onAdmitted,
}: {
  code: string;
  token: string;
  onAdmitted: () => void;
}) {
  const [phase, setPhase] = useState<Phase>('kicked');
  const [error, setError] = useState<string | null>(null);

  async function ask() {
    try {
      const { status } = await api.requestRejoin(code, token);
      setPhase(status === 'kicked' ? 'pending' : status);
      setError(null);
    } catch (err) {
      setPhase('error');
      setError(err instanceof Error ? err.message : 'Could not reach the server.');
    }
  }

  // The same call files the request and reports on it, so polling is just asking again.
  useEffect(() => {
    if (phase !== 'pending') return;
    const t = setTimeout(() => void ask(), POLL_MS);
    return () => clearTimeout(t);
    // `ask` closes over nothing that changes between polls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  useEffect(() => {
    if (phase === 'admitted') onAdmitted();
  }, [phase, onAdmitted]);

  return (
    <div className="centered">
      <div className="card col" role="status" aria-live="polite">
        <h1>You were removed</h1>
        {phase === 'kicked' || phase === 'error' ? (
          <>
            <p className="muted" style={{ marginTop: 0 }}>
              The host removed you from this game. You can ask to come back; the host decides.
            </p>
            {error ? <p className="error-text">{error}</p> : null}
            <button className="btn btn-primary btn-lg" onClick={() => void ask()}>
              Request to Rejoin
            </button>
          </>
        ) : null}
        {phase === 'pending' ? (
          <p className="muted" style={{ marginTop: 0 }}>
            Request sent. Waiting for the host…
          </p>
        ) : null}
        {phase === 'denied' ? (
          <p className="muted" style={{ marginTop: 0 }}>
            The host did not let you back in.
          </p>
        ) : null}
        {phase === 'admitted' ? <p className="muted">You’re back in. Rejoining…</p> : null}
        <Link className="btn" to="/join">
          Join another game
        </Link>
      </div>
    </div>
  );
}
